import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link, navigate, useStaticQuery } from 'gatsby';
import LoadingOverlay from '../../../../common/loading-overlay';
import { Alert, Button, List, TextInput } from '@pella/sash-framework';
import CTA from '../../../../common/cta';
import { useMachine } from '@xstate/react';
import { loginMachine } from '../../../../../machines/account/login/loginMachine';
import { useAccount } from '../../../../../hooks/use-account';
import { fromPromise } from 'xstate';
import { useOktaAuth } from '../../../../../hooks/use-okta-auth';
import Cookies from 'universal-cookie';

const LoginForm = (props) => {
	const csPassword = useStaticQuery(query).contentstackPassword;
	const { box_heading, cta_heading, cta_2, secondary } = csPassword;
	const { type: loginType, requestedUrl, location, onAuthCloseModal } = props;
	const { login } = useAccount();
	const { setProfile, setAuthState } = useOktaAuth();
	const getCookieContext = () => new Cookies();

	const [snapshot, send] = useMachine(
		loginMachine.provide({
			actors: {
				login: fromPromise(() => login({ ...context.loginValues })),
			},
			actions: {
				resetOkta: () => {
					setProfile({
						date: Date.now(),
						isPro: false,
					});
					setAuthState({});
				},
				postLoginAction: ({ event }) => {
					if (requestedUrl === '/shop/checkout/' && event?.output?.isPro) {
						onAuthCloseModal();
					} else if (requestedUrl) {
						navigate(requestedUrl);
					}

					if (!requestedUrl && loginType === 'isModal') {
						onAuthCloseModal();
					}
				},
			},
		}),
		{
			input: {
				loginType,
				requestedUrl,
			},
		}
	);
	const { context, value: currentState } = snapshot;
	const { errors, loginValues } = context;

	const handleInputChange = (e) => {
		e.preventDefault();
		send({
			type: 'INPUT_CHANGE',
			params: { name: e.target.name, value: e.target.value },
		});
	};

	const submitLogin = (e) => {
		e.preventDefault();
		Object.keys(errors).length === 0 && send({ type: 'SUBMIT_LOGIN' });
	};

	const redirectURL = () => {
		const urlFromLocation = loginType !== 'isModal' ? location?.state?.redirectURL : null;

		if (requestedUrl) return { redirectURL: requestedUrl };
		if (urlFromLocation) return { redirectURL: urlFromLocation };

		if (typeof window !== 'undefined' && window.location.pathname === '/shop/cart/') {
			const searchParams = window.location.search || '';
			return { redirectURL: `${window.location.pathname}${searchParams}` };
		}

		return null;
	};

	const createAccountReferrer = () => {
		if (loginType !== 'isModal') return;

		const cookies = getCookieContext();
		const path = window.location.pathname;

		if (path.includes('/cart')) {
			cookies.set('CreateAccountReferrer', 'checkout', {
				path: '/',
				maxAge: 86400,
			});
		} else if (path.includes('/shop/configure') || path.includes('/shop/summary')) {
			cookies.set('CreateAccountReferrer', 'project', {
				path: '/',
				maxAge: 86400,
			});
		}
	};

	return (
		<div className={loginType === 'isDropdown' ? 'px-2.5 pb-2.5 pt-1.5' : ''}>
			{currentState === 'loggingIn' && <LoadingOverlay fixed={!(loginType === 'isModal' || loginType === 'isDropdown')} />}

			{Object.entries(errors).length > 0 && (
				<Alert
					type='error'
					className='mb-4'
					hideClose
				>
					<List
						bullets='none'
						className='m-0 p-0'
					>
						{Object.entries(errors).map(([field, message], index) => (
							<li key={index}>{message}</li>
						))}
					</List>
				</Alert>
			)}

			<p className='col-start-2 mb-1.5 text-right text-xs'>
				Required Fields<span className='pl-0.5 text-red'>*</span>
			</p>

			<form
				onSubmit={submitLogin}
				className={`mb-6 ${loginType === 'isDropdown' ? '' : 'grid grid-cols-1 sm:grid-cols-2 sm:gap-x-4'}`}
				noValidate
			>
				{box_heading.map((box, index) => (
					<TextInput
						key={index}
						type={index === 0 ? 'text' : 'password'}
						id={index === 0 ? 'username' : 'password'}
						name={index === 0 ? 'username' : 'password'}
						labelText={box.heading}
						required={box.required}
						value={loginValues[index === 0 ? 'username' : 'password']}
						onChange={handleInputChange}
						error={errors?.[index === 0 ? 'username' : 'password']}
						autoComplete={index === 0 ? 'email' : 'current-password'}
						errorText={errors[index === 0 ? 'username' : 'password']}
						minLength={index === 0 ? undefined : '8'}
					/>
				))}

				<Button
					type='submit'
					name='signin'
					className={loginType === 'isDropdown' ? 'mb-4 !w-full' : 'mb-4 sm:mb-0'}
				>
					{cta_heading}
				</Button>

				<CTA data={cta_2[0]} />
			</form>

			{loginType !== 'isOrderTracker' && (
				<>
					<h2 className='text-subtitle mb-3'>{secondary.heading}</h2>
					<p className='mb-2.5 max-w-[50ch]'>{secondary.subheading}</p>
					<div className={loginType === 'isDropdown' ? '' : 'grid grid-cols-1 sm:grid-cols-2 sm:gap-x-4'}>
						<CTA
							className='!w-full'
							data={secondary.ctas[0]}
							state={redirectURL()}
							component={Link}
							onClick={createAccountReferrer}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default LoginForm;

LoginForm.propTypes = {
	type: PropTypes.string.isRequired,
	requestedUrl: PropTypes.string,
	location: PropTypes.object,
	onAuthCloseModal: PropTypes.func,
};

const query = graphql`
	query {
		contentstackPassword(uid: { eq: "blt80d20b6a4ee886a4" }) {
			box_heading {
				heading
				required
			}
			cta_heading
			cta_2 {
				...CTA
			}
			secondary {
				heading
				subheading
				ctas {
					...CTA
				}
			}
		}
	}
`;
